import { useEffect, useState } from 'react'
import { useIntlayer } from 'react-intlayer'

type ImageQualityWarningProps = {
  show: boolean
  imageName: string
  dpi: number
  onDismiss: () => void
  isMobile?: boolean
}

export const ImageQualityWarning = ({
  show,
  imageName,
  dpi,
  onDismiss,
  isMobile = false,
}: ImageQualityWarningProps) => {
  const { title, messagePart1, messagePart2, messagePart3, tip } =
    useIntlayer('imageQualityWarning')
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (show) {
      setIsVisible(true)
      const timer = setTimeout(() => {
        setIsVisible(false)
        onDismiss()
      }, 8000) // Show for 8 seconds
      return () => clearTimeout(timer)
    }
  }, [show, onDismiss])

  if (!isVisible) return null

  return (
    <div
      className={`fixed ${
        isMobile ? 'top-20 left-4 right-4' : 'top-4 left-1/2 transform -translate-x-1/2'
      } bg-yellow-50 border border-yellow-200 text-yellow-900 px-6 py-4 rounded-lg shadow-lg z-[9999] max-w-md`}
    >
      <div className="flex items-start gap-3">
        <svg
          className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <div className="flex-1">
          <h3 className="text-sm font-semibold mb-1">{title}</h3>
          <p className="text-sm">
            {messagePart1}
            {imageName}
            {messagePart2}
            {dpi}
            {messagePart3}
          </p>
          <p className="text-xs mt-2 text-yellow-700">{tip}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false)
            onDismiss()
          }}
          className="text-yellow-600 hover:text-yellow-800 focus:outline-none"
        >
          <svg
            className="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  )
}
